import Cookies from 'js-cookie'
import {z} from 'zod'

import {
    bigformActioned,
    BigformActionedAction,
    bigformViewed,
    bigformSubmitted,
    demoBookingSubmitted,
    demoBookingViewed,
    googleAdsOptimisationSignal,
    performanceMarketingSignupActioned,
    PerformanceMarketingSignupActionedAction,
    signupFunnelSignalEvent,
} from '@/analytics/segment'
import {cookiesMap} from '@/config/cookies'
import CompanySize from '@/types/company-size'
import {identify, getUserTraits} from '@/utils/analytics'
import compactObject from '@/utils/compact-object'
import {getConversionValues} from '@/utils/conversion-values'
import exhaustiveCheck from '@/utils/exhaustive-check'
import {getHashedString} from '@/utils/get-hashed-string'
import {isGenericEmail} from '@/utils/is-generic-email'

import type {State as HubspotGlobalFormState} from './global/hubspot-global-form'
import type {State as PartnerBigformFormState} from './partner/partner-bigform'
import type {State as HubspotFormState} from '../hubspot-form'
import type {CountryCode, CountryCode as SupportedCountryCode} from '@/locale/locales'

import conversionScenariosJSON from './conversion-scenarios.json'

export const OTHER = 'Other' as const

export enum FunnelStage {
    BigformReached = 'bigform_reached',
    BigformSubmitted = 'bigform_submitted',
    PleoDemoReached = 'pleodemo_reached',
    PleoDemoBooked = 'pleodemo_booked',
}

interface BigformReachedArgs {
    country: SupportedCountryCode
    trackQualifyingLeads?: boolean
}

export const bigformReached = (data: BigformReachedArgs) => {
    const params = new URLSearchParams(window.location?.search)
    const email = params.get('email')

    const {trackQualifyingLeads} = data

    email && identify({email})
    bigformActioned({action: BigformActionedAction.Started})
    bigformViewed({})

    if (trackQualifyingLeads && !isGenericEmail(email)) {
        const userTraits = getUserTraits()
        performanceMarketingSignupActioned(
            {
                action: PerformanceMarketingSignupActionedAction.Started,
                value: 1,
                fbc: Cookies.get(cookiesMap.metaClickId) || '',
                fbp: Cookies.get(cookiesMap.metaBrowserId) || '',
                ttclid: Cookies.get(cookiesMap.tikTokPixelId) || '',
            },
            userTraits,
        )
    }
}

type BigformSubmittedArgs = HubspotFormState | HubspotGlobalFormState | PartnerBigformFormState

export const bigformCompleted = (data: BigformSubmittedArgs) => {
    const externalId = Math.random().toString(16).substring(2)
    const traits = compactObject({
        email: data.email,
        phone: 'phoneNumber' in data && data.phoneNumber,
        name: 'userFullName' in data && data.userFullName,
        firstName: 'firstName' in data && data.firstName,
        lastName: 'lastName' in data && data.lastName,
        marketingEmails: 'marketingEmails' in data && data.marketingEmails,
        form: 'DirectBigForm',
        external_id: window.location.pathname.includes('/demo') && externalId,
        externalId: window.location.pathname.includes('/demo') && externalId,
        company: {
            name: 'companyName' in data && data.companyName,
            employee_count:
                'companySize' in data &&
                Number(data.companySize.split('-').pop()?.replace('+', '')),
        },
    })

    identify(traits)
    bigformActioned({action: BigformActionedAction.Completed})
    bigformSubmitted(data)
}

// Track interaction with fields (e.g. typed character in text input, or selection in select)
// We only track interaction with each element once per session
const fieldsTracked: Record<string, boolean> = {}
export const bigformInteracted = (field: string) => {
    if (fieldsTracked[field]) {
        return
    }
    bigformActioned({action: BigformActionedAction.Interacted, field})
    fieldsTracked[field] = true
}

interface PleoDemoReachedArgs {
    email?: string
}

export const pleoDemoReached = async ({email}: PleoDemoReachedArgs) => {
    const hashedEmail = await getHashedString(email || '')

    demoBookingViewed({})
    signupFunnelSignalEvent({
        fbc: Cookies.get(cookiesMap.metaClickId) || '',
        fbp: Cookies.get(cookiesMap.metaBrowserId) || '',
        gclid: Cookies.get(cookiesMap.googleClickIdentifier) || '',
        conversion_flow: 'demo_booking',
        uuid: Cookies.get(cookiesMap.userUniqueIdentifier) || '',
        currency: 'EUR',
        isGeneric: isGenericEmail(email),
        milestone: 'DEMO_BOOKING_VIEWED',
        email,
        hashed_email: hashedEmail,
    })
}

interface PleoDemoBookedArgs {
    country?: SupportedCountryCode
    companySize?: CompanySize
    email?: string
}

export const pleoDemoBooked = async ({companySize, country, email}: PleoDemoBookedArgs) => {
    const hashedEmail = await getHashedString(email || '')

    const conversionScenario = getConversionScenario({
        country,
        companySize,
        email,
        marketingMilestone: 'DEMO_BOOKING_SUBMITTED',
    })

    signupFunnelSignalEvent({
        fbc: Cookies.get(cookiesMap.metaClickId) || '',
        fbp: Cookies.get(cookiesMap.metaBrowserId) || '',
        gclid: Cookies.get(cookiesMap.googleClickIdentifier) || '',
        conversion_flow: 'demo_booking',
        uuid: Cookies.get(cookiesMap.userUniqueIdentifier) || '',
        currency: 'EUR',
        company_size: companySize,
        companyCountry: country,
        isGeneric: isGenericEmail(email),
        milestone: 'DEMO_BOOKING_SUBMITTED',
        optimise: conversionScenario.optimise,
        metaOptimise: conversionScenario.meta_optimise,
        googleOptimise: conversionScenario.google_optimise,
        optimiseTest1: conversionScenario.optimise_test1,
        optimiseTest2: conversionScenario.optimise_test1,
        optimiseTest3: conversionScenario.optimise_test3,
        value: conversionScenario.value,
        email,
        hashed_email: hashedEmail,
    })

    demoBookingSubmitted({
        fbc: Cookies.get(cookiesMap.metaClickId) || '',
        fbp: Cookies.get(cookiesMap.metaBrowserId) || '',
    })
}

interface GoogleAdsConversionArgs {
    country?: SupportedCountryCode
    companySize?: CompanySize
}

export const googleAdsConversion = ({country, companySize}: GoogleAdsConversionArgs) => {
    if (country && companySize) {
        const conversionValues = getConversionValues(companySize, country)
        const userTraits = getUserTraits()

        if (conversionValues) {
            googleAdsOptimisationSignal(conversionValues, userTraits)
        }
    }
}

export type ConversionScenariosInputItem = {
    funnel_step: string
    company_country: string
    company_size: string
    is_generic: boolean
    signal_value: number
    optimise?: boolean
    meta_optimise?: boolean
    google_optimise?: boolean
    optimise_test1?: boolean
    optimise_test2?: boolean
    optimise_test3?: boolean
}

export type GetConversionScenarioArgs = {
    country: CountryCode | undefined
    companySize: CompanySize | undefined | null
    marketingMilestone: MarketingMilestone | undefined
    email: string | undefined
}

type GetConversionScenarioReturn = {
    value: number
    optimise: boolean
    meta_optimise: boolean
    google_optimise: boolean
    optimise_test1: boolean
    optimise_test2: boolean
    optimise_test3: boolean
}

/**
 * Takes in a JSON file of all possible combinations of properties and attempts to find and return a matching scenario.
 *
 * Includes some fallback values for expected missing scenarios (e.g. company size won't exist until late in the signup
 * flow, so we default to using the scenarios with size 1).
 *
 * Unexpected cases will return a value of 0 and report an error.
 */
export const getConversionScenarioFromJSON = (
    conversionScenariosInput: ConversionScenariosInputItem[],
) => {
    // The countries currently represented in the conversion scenarios JSON file
    const conversionScenarioCountries = [
        ...new Set(conversionScenariosInput.map((c) => c.company_country)),
    ]

    return ({
        country,
        companySize,
        marketingMilestone,
        email,
    }: GetConversionScenarioArgs): GetConversionScenarioReturn => {
        const defaultReturn: GetConversionScenarioReturn = {
            value: 0,
            optimise: false,
            meta_optimise: false,
            google_optimise: false,
            optimise_test1: false,
            optimise_test2: false,
            optimise_test3: false,
        }
        const schemaParseResult = jsonSchema.safeParse(conversionScenariosInput)

        // If the input doesn't match the schema, report an error
        if (!schemaParseResult.success) {
            console.error('Source JSON file does not match the expected schema')

            return defaultReturn
        }

        const conversionScenarios = schemaParseResult.data

        // This should always be defined. If not, report an error
        if (!marketingMilestone) {
            console.error('Missing marketing milestone when evaluating conversion scenario')

            return defaultReturn
        }

        // This should always be defined. If not, report an error
        if (!country) {
            console.error('Missing country when evaluating conversion scenario')

            return defaultReturn
        }

        // If called with no company size, default to size '1'
        const companySizeWithDefault = companySize ?? CompanySize.Size_1

        let countryWithDefault
        // If called with a country that IS in the conversion scenarios JSON file..
        if (conversionScenarioCountries.includes(country.toUpperCase())) {
            countryWithDefault = country.toUpperCase() // ..use the same country's conversion scenarios
        } else if (country === 'ie') {
            countryWithDefault = 'gb' // ..use the GB conversion scenarios.
        } else {
            countryWithDefault = 'nl' // Else, use the NL conversion scenarios.
        }
        // If no email is provided, default to generic being true
        const isGenericWithDefault = email ? isGenericEmail(email) : true
        // Get the funnel step that's mapped to the provided marketing milestone
        const mappedMarketingMilestone = getFunnelStepFromMilestone(marketingMilestone)

        const conversionScenarioResults = conversionScenarios.filter(
            (c: {
                funnel_step: string
                company_country: string
                company_size: CompanySize
                is_generic: boolean
            }) =>
                c.funnel_step === mappedMarketingMilestone &&
                c.company_country === countryWithDefault &&
                c.company_size === companySizeWithDefault &&
                c.is_generic === isGenericWithDefault,
        )

        // If we didn't find a matching scenario, report an error
        if (!conversionScenarioResults.length) {
            console.error('No matching conversion scenario found')

            return defaultReturn
        }

        // If we find more than 1 matching scenario
        if (conversionScenarioResults.length > 1) {
            // Filter out exact duplicates
            const uniqueConversionScenarioResults = conversionScenarioResults.filter(
                (obj1: any, i: any, arr: any[]) => {
                    return (
                        arr.findIndex((obj2) => JSON.stringify(obj2) === JSON.stringify(obj1)) === i
                    )
                },
            )

            // If we still have more than 1 match, report an error (could be e.g. same properties but different signal
            // values)
            if (uniqueConversionScenarioResults.length > 1) {
                console.error('More than one matching conversion scenario found')

                return defaultReturn
            }
        }

        const conversionScenarioResult = conversionScenarioResults[0]

        return {
            value: conversionScenarioResult.signal_value,
            optimise: Boolean(conversionScenarioResult.optimise) ?? false,
            meta_optimise: Boolean(conversionScenarioResult.meta_optimise) ?? false,
            google_optimise: Boolean(conversionScenarioResult.google_optimise) ?? false,
            optimise_test1: Boolean(conversionScenarioResult.optimise_test1) ?? false,
            optimise_test2: Boolean(conversionScenarioResult.optimise_test2) ?? false,
            optimise_test3: Boolean(conversionScenarioResult.optimise_test3) ?? false,
        }
    }
}

export const getConversionScenario = getConversionScenarioFromJSON(conversionScenariosJSON)

export const conversionScenarioFunnelSteps = [
    'demo_booking_viewed',
    'demo_booking_submitted',
] as const

// prettier-ignore
type ConversionScenarioFunnelStep = (typeof conversionScenarioFunnelSteps)[number]

// This is the schema we expect for the source JSON file
export const jsonSchema = z
    .array(
        z.object({
            funnel_step: z.enum(conversionScenarioFunnelSteps),
            company_country: z.string(),
            company_size: z.nativeEnum(CompanySize),
            is_generic: z.boolean(),
            signal_value: z.number(),
            optimise: z.boolean().optional(),
            meta_optimise: z.boolean().optional(),
            google_optimise: z.boolean().optional(),
            optimise_test1: z.boolean().optional(),
            optimise_test2: z.boolean().optional(),
            optimise_test3: z.boolean().optional(),
        }),
    )
    .nonempty()

export const marketingMilestones = ['DEMO_BOOKING_VIEWED', 'DEMO_BOOKING_SUBMITTED'] as const

// prettier-ignore
export type MarketingMilestone = (typeof marketingMilestones)[number]

const getFunnelStepFromMilestone = (
    milestone: MarketingMilestone,
): ConversionScenarioFunnelStep => {
    switch (milestone) {
        case 'DEMO_BOOKING_VIEWED':
            return 'demo_booking_viewed'
        case 'DEMO_BOOKING_SUBMITTED':
            return 'demo_booking_submitted'
        default:
            return exhaustiveCheck(milestone)
    }
}
